import contains from 'dom-helpers/query/contains';
import 'dom-helpers/query/closest';
import 'dom-helpers/events';
import { addEventListener, isObjectsColliding, getBoundsForNode, getEventCoordinates, isOverContainer, normalizeDistance } from './utils/domHelpers.js';

// @ts-ignore
const clickTolerance = 5;
const clickInterval = 250;
class TimeSelection {
  constructor(node, {
    global = false,
    longPressThreshold = 250
  } = {}) {
    this.isDetached = false;
    this.container = node;
    this.globalMouse = !node || global;
    this.longPressThreshold = longPressThreshold;
    this.listeners = Object.create(null);
    this.handleInitialEvent = this.handleInitialEvent.bind(this);
    this.handleMoveEvent = this.handleMoveEvent.bind(this);
    this.handleTerminatingEvent = this.handleTerminatingEvent.bind(this);
    this.keyListener = this.keyListener.bind(this);
    this.dropFromOutsideListener = this.dropFromOutsideListener.bind(this);
    this.onTouchMoveWindowListener = addEventListener('touchmove', () => {},
    // @ts-ignore
    window);
    this.onKeyDownListener = addEventListener('keydown', this.keyListener);
    this.onKeyUpListener = addEventListener('keyup', this.keyListener);
    this.onDropFromOutsideListener = addEventListener('drop', this.dropFromOutsideListener);
    this.addInitialEventListener();
  }
  on(type, handler) {
    const handlers = this.listeners[type] || (this.listeners[type] = []);
    handlers.push(handler);
    return {
      remove() {
        const idx = handlers.indexOf(handler);
        if (idx !== -1) handlers.splice(idx, 1);
      }
    };
  }
  emit(type, ...args) {
    let result;
    const handlers = this.listeners[type] || [];
    handlers.forEach(fn => {
      if (result === undefined) result = fn(...args);
    });
    return result;
  }
  teardown() {
    this.isDetached = true;
    this.listeners = Object.create(null);
    if (this.onTouchMoveWindowListener) this.onTouchMoveWindowListener.remove();
    if (this.onInitialEventListener) this.onInitialEventListener.remove();
    if (this.onEndListener) this.onEndListener.remove();
    if (this.onEscListener) this.onEscListener.remove();
    if (this.onMoveListener) this.onMoveListener.remove();
    if (this.onKeyUpListener) this.onKeyUpListener.remove();
    if (this.onKeyDownListener) this.onKeyDownListener.remove();
  }
  isSelected(node) {
    const box = this.selectRect;
    if (!box || !this.selecting) return false;
    // @ts-ignore
    return isObjectsColliding(box, getBoundsForNode(node));
  }
  filter(items) {
    const box = this.selectRect;
    if (!box || !this.selecting) return [];
    return items.filter(this.isSelected, this);
  }
  addLongPressListener(handler, initialEvent) {
    let timer = null;
    let touchMoveListener = null;
    let touchEndListener = null;
    const handleTouchStart = event => {
      timer = setTimeout(() => {
        cleanup();
        handler(event);
      }, this.longPressThreshold);
      touchMoveListener = addEventListener('touchmove', () => cleanup());
      touchEndListener = addEventListener('touchend', () => cleanup());
    };
    const touchStartListener = addEventListener('touchstart', handleTouchStart);
    const cleanup = () => {
      if (timer) {
        clearTimeout(timer);
      }
      if (touchMoveListener) {
        touchMoveListener.remove();
      }
      if (touchEndListener) {
        touchEndListener.remove();
      }
      timer = null;
      touchMoveListener = null;
      touchEndListener = null;
    };
    if (initialEvent) {
      handleTouchStart(initialEvent);
    }
    return {
      remove() {
        cleanup();
        // @ts-ignore
        touchStartListener.remove();
      }
    };
  }
  addInitialEventListener() {
    const mouseDownListener = addEventListener('mousedown', e => {
      this.onInitialEventListener.remove();
      this.handleInitialEvent(e);
      this.onInitialEventListener = addEventListener('mousedown', this.handleInitialEvent);
    });
    const touchStartListener = addEventListener('touchstart', e => {
      this.onInitialEventListener.remove();
      this.onInitialEventListener = this.addLongPressListener(this.handleInitialEvent, e);
    });
    this.onInitialEventListener = {
      remove() {
        // @ts-ignore
        mouseDownListener.remove();
        // @ts-ignore
        touchStartListener.remove();
      }
    };
  }
  dropFromOutsideListener(e) {
    const {
      pageX,
      pageY,
      clientX,
      clientY
    } = getEventCoordinates(e);
    this.emit('dropFromOutside', {
      clientX,
      clientY,
      x: pageX,
      y: pageY
    });
    e.preventDefault();
  }
  handleInitialEvent(e) {
    if (this.isDetached) {
      return;
    }
    const {
      clientX,
      clientY,
      pageX,
      pageY
    } = getEventCoordinates(e);
    const node = this.container;
    let collides;
    let offsetData;

    // Right clicks
    if (e.which === 3 || e.button === 2 || !isOverContainer(node, clientX, clientY)) {
      return;
    }
    if (!this.globalMouse && node && !contains(node, e.target)) {
      const {
        top,
        left,
        bottom,
        right
      } = normalizeDistance(0);
      offsetData = getBoundsForNode(node);
      collides = isObjectsColliding({
        top: offsetData.top - top,
        left: offsetData.left - left,
        bottom: offsetData.bottom + bottom,
        right: offsetData.right + right
      }, {
        top: pageY,
        left: pageX
      });
      if (!collides) return;
    }
    const result = this.emit('beforeSelect', this.initialEventData = {
      clientX,
      clientY,
      isTouch: /^touch/.test(e.type),
      x: pageX,
      y: pageY,
      event: e
    });
    if (result.isEditable === false) {
      this.onEndListener = addEventListener('mouseup', this.handleTerminatingEvent);
      return;
    }
    if (result.isResizeElement) {
      const eventCoordinates = result.eventNode.getBoundingClientRect();
      this.initialEventData = {
        clientX: eventCoordinates.x,
        clientY: eventCoordinates.y,
        isTouch: /^touch/.test(e.type),
        x: eventCoordinates.x,
        y: result.isStart ? eventCoordinates.bottom : eventCoordinates.y,
        event: e
      };
    }
    this.initialEventData.isResizeElement = result.isResizeElement;
    switch (e.type) {
      case 'mousedown':
        this.onEndListener = addEventListener('mouseup', this.handleTerminatingEvent);
        this.onEscListener = addEventListener('keydown', this.handleTerminatingEvent);
        if (!result.isSingleTimeSelection) {
          this.onMoveListener = addEventListener('mousemove', this.handleMoveEvent);
        }
        break;
      case 'touchstart':
        this.handleMoveEvent(e);
        this.onEndListener = addEventListener('touchend', this.handleTerminatingEvent);
        if (!result.isSingleTimeSelection) {
          this.onMoveListener = addEventListener('touchmove', this.handleMoveEvent);
        }
        break;
    }
  }
  handleTerminatingEvent(e) {
    const {
      pageX,
      pageY
    } = getEventCoordinates(e);
    this.selecting = false;
    if (this.onEndListener) {
      this.onEndListener.remove();
    }
    if (this.onMoveListener) {
      this.onMoveListener.remove();
    }
    if (!this.initialEventData) return;
    const inRoot = !this.container || contains(this.container, e.target);
    const bounds = this.selectRect;
    const click = this.isClick(pageX, pageY);
    this.initialEventData = null;
    if (e.key === 'Escape') {
      this.emit('reset');
    }
    if (!inRoot) {
      this.emit('reset');
    }
    if (click && inRoot) {
      this.handleClickEvent(e);
    }

    // User drag-clicked in the Selectable area
    if (!click) {
      this.emit('select', bounds);
    }
  }
  handleClickEvent(e) {
    const {
      pageX,
      pageY,
      clientX,
      clientY
    } = getEventCoordinates(e);
    const now = new Date().getTime();
    if (this.lastClickData && now - this.lastClickData.timestamp < clickInterval) {
      // Double click event
      this.lastClickData = null;
      return this.emit('doubleClick', {
        clientX,
        clientY,
        x: pageX,
        y: pageY
      });
    }

    // Click event
    this.lastClickData = {
      timestamp: now
    };
    return this.emit('click', {
      clientX,
      clientY,
      x: pageX,
      y: pageY
    });
  }
  handleMoveEvent(e) {
    if (this.initialEventData === null || this.isDetached) {
      return;
    }
    const {
      x,
      y
    } = this.initialEventData;
    const {
      pageX,
      pageY
    } = getEventCoordinates(e);
    const w = Math.abs(x - pageX);
    const h = Math.abs(y - pageY);
    const left = Math.min(pageX, x);
    const top = Math.min(pageY, y);
    const old = this.selecting;
    if (this.isClick(pageX, pageY) && !old && !(w || h)) {
      return;
    }
    this.selecting = true;
    this.selectRect = {
      top,
      left,
      x: pageX,
      y: pageY,
      right: left + w,
      bottom: top + h
    };
    if (!old) {
      this.emit('selectStart', this.initialEventData);
    }
    if (!this.isClick(pageX, pageY)) this.emit('selecting', this.selectRect);
    e.preventDefault();
  }

  // eslint-disable-next-line class-methods-use-this
  keyListener(e) {
    return e.metaKey || e.ctrlKey;
  }
  isClick(pageX, pageY) {
    const {
      x,
      y,
      isTouch
    } = this.initialEventData;
    return !isTouch && Math.abs(pageX - x) <= clickTolerance && Math.abs(pageY - y) <= clickTolerance;
  }
}

export default TimeSelection;
